export const CALENDAR_COLORS = {
  blue: "#b8d7ee",
  green: "#c8dfc5",
  orange: "#ffd1a6",
  purple: "#dfb3eb",
  pink: "#f5bfd3",
  red: "#f2b0aa",
  teal: "#b9e7e7",
  slate: "#d0d7df",
} as const;

export type CalendarColor = keyof typeof CALENDAR_COLORS;
export type CalendarRepeatType = "none" | "daily" | "weekly" | "monthly" | "days";

export interface CalendarEventInput {
  name: string;
  startDate: string;
  endDate: string | null;
  startTime: string;
  endTime: string;
  color: CalendarColor;
  repeatType: CalendarRepeatType;
  weeklyDays: number[];
  monthlyDay: number | null;
  intervalDays: number | null;
}

export interface CalendarSkipRange {
  date: string;
  days: number;
}

export interface CalendarEventSeries extends CalendarEventInput {
  id: number;
  skippedRanges: CalendarSkipRange[];
  skippedDates: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CalendarOccurrence {
  id: string;
  seriesId: number;
  name: string;
  date: string;
  start: string;
  end: string;
  color: CalendarColor;
}

const calendarDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
const millisecondsPerDay = 86_400_000;

export function calendarDay(value: string): number {
  const match = calendarDatePattern.exec(value);
  if (!match) throw new Error("Date must use YYYY-MM-DD format");
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
    throw new Error("Date is not a valid calendar day");
  }
  return Math.floor(parsed.getTime() / millisecondsPerDay);
}

export function calendarDate(day: number): string {
  return new Date(day * millisecondsPerDay).toISOString().slice(0, 10);
}

export function calendarRangeDates(date: string, days: number): string[] {
  const start = calendarDay(date);
  return Array.from({ length: days }, (_, offset) => calendarDate(start + offset));
}

export function isCalendarDate(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try { calendarDay(value); return true; } catch { return false; }
}

export function isCalendarTime(value: unknown): value is string {
  return typeof value === "string" && timePattern.test(value);
}

export function localCalendarDate(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function startOfSundayWeek(value: string): string {
  const day = calendarDay(value);
  return calendarDate(day - new Date(day * millisecondsPerDay).getUTCDay());
}

function occurrence(series: CalendarEventSeries, date: string): CalendarOccurrence {
  return {
    id: `${series.id}:${date}`,
    seriesId: series.id,
    name: series.name,
    date,
    start: `${date}T${series.startTime}:00`,
    end: `${date}T${series.endTime}:00`,
    color: series.color,
  };
}

function monthlyMatch(day: number, monthlyDay: number): boolean {
  return new Date(day * millisecondsPerDay).getUTCDate() === monthlyDay;
}

export function expandCalendarEvent(
  series: CalendarEventSeries,
  rangeStart: string,
  rangeEnd: string,
): CalendarOccurrence[] {
  const requestedStart = calendarDay(rangeStart);
  const requestedEnd = calendarDay(rangeEnd);
  if (requestedEnd < requestedStart) return [];

  const start = calendarDay(series.startDate);
  const configuredEnd = series.endDate ? calendarDay(series.endDate) : null;
  if (series.repeatType === "none") {
    const last = configuredEnd === null ? start : Math.max(start, configuredEnd);
    const first = Math.max(start, requestedStart);
    const requestedLast = Math.min(last, requestedEnd);
    const skippedDates = new Set(series.skippedDates);
    const results: CalendarOccurrence[] = [];
    for (let day = first; day <= requestedLast; day += 1) {
      const date = calendarDate(day);
      if (!skippedDates.has(date)) results.push(occurrence(series, date));
    }
    return results;
  }
  if (configuredEnd !== null && configuredEnd <= start) {
    return start >= requestedStart && start <= requestedEnd && !series.skippedDates.includes(series.startDate)
      ? [occurrence(series, series.startDate)]
      : [];
  }

  const last = configuredEnd === null ? requestedEnd : Math.min(configuredEnd, requestedEnd);
  const first = Math.max(start, requestedStart);
  if (last < first) return [];
  const weeklyDays = new Set(series.weeklyDays);
  const interval = series.intervalDays ?? 1;
  const monthlyDay = series.monthlyDay ?? 1;
  const skippedDates = new Set(series.skippedDates);
  const results: CalendarOccurrence[] = [];

  for (let day = first; day <= last; day += 1) {
    const matches = series.repeatType === "daily"
      || (series.repeatType === "weekly" && weeklyDays.has(new Date(day * millisecondsPerDay).getUTCDay()))
      || (series.repeatType === "monthly" && monthlyMatch(day, monthlyDay))
      || (series.repeatType === "days" && (day - start) % interval === 0);
    const date = calendarDate(day);
    if (matches && !skippedDates.has(date)) results.push(occurrence(series, date));
  }
  return results;
}

export function isRepeatingCalendarEvent(series: Pick<CalendarEventInput, "repeatType" | "startDate" | "endDate">): boolean {
  return series.repeatType !== "none" && (series.endDate === null || calendarDay(series.endDate) > calendarDay(series.startDate));
}

export function calendarEventOccursOn(series: CalendarEventSeries, date: string): boolean {
  return isCalendarDate(date) && expandCalendarEvent({ ...series, skippedDates: [] }, date, date).length === 1;
}

export function expandCalendarEvents(
  series: CalendarEventSeries[],
  rangeStart: string,
  rangeEnd: string,
): CalendarOccurrence[] {
  return series
    .flatMap(event => expandCalendarEvent(event, rangeStart, rangeEnd))
    .sort((left, right) => left.start.localeCompare(right.start) || left.name.localeCompare(right.name) || left.seriesId - right.seriesId);
}
